// Authentication bridge utilities for cross-domain auth transfer

/**
 * Extract auth data from URL parameters
 */
export const extractAuthFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const authData = {};
  
  try {
    // Decode URL parameters
    if (urlParams.has('token')) {
      authData.token = atob(urlParams.get('token'));
    }
    if (urlParams.has('email')) {
      authData.userEmail = atob(urlParams.get('email'));
    }
    if (urlParams.has('userData')) {
      authData.userData = JSON.parse(atob(urlParams.get('userData')));
    }
    if(urlParams.has('first_name')) {
      authData.first_name = atob(urlParams.get('first_name'));
    }
    if (urlParams.has('last_name')) {
      authData.last_name = atob(urlParams.get('last_name'));
    }
    if (urlParams.has('api_key')) {
      authData.api_key = atob(urlParams.get('api_key'));
    }
    
    console.log('Auth data extracted from URL:', authData);
    return authData;
  } catch (error) {
    console.error('Error extracting auth data from URL:', error);
    return null;
  }
};

/**
 * Store auth data in localStorage
 */
export const storeAuthData = (authData) => {
  try {
    if (authData.api_key) {
      localStorage.setItem('api_key', authData.api_key);
    }
    if (authData.email) {
      localStorage.setItem('email', authData.email);
    }
    if (authData.first_name) {
      localStorage.setItem('first_name', authData.first_name);
    }
    if (authData.last_name) {
      localStorage.setItem('last_name', authData.last_name);
    }
    if (authData.userData) {
      localStorage.setItem('user-data', JSON.stringify(authData.userData));
    }

    console.log('Auth data stored in localStorage');
    return true;
  } catch (error) {
    console.error('Error storing auth data:', error);
    return false;
  }
};

/**
 * Clean URL parameters after extracting auth data
 */
export const cleanURLParams = () => {
  const url = new URL(window.location);
  url.search = '';
  window.history.replaceState({}, document.title, url.pathname);
};

/**
 * Get allowed origins for postMessage
 * TODO: Configure this based on your environment
 */
const getAllowedOrigins = () => {
  // Add your allowed origins here
  return [
    window.location.origin, // Current origin
    // Add other trusted origins as needed
  ];
};

/**
 * Listen for postMessage auth data from parent window
 */
export const setupPostMessageListener = (callback) => {
  const allowedOrigins = getAllowedOrigins();

  const handleMessage = (event) => {
    // Verify origin for security
    if (!allowedOrigins.includes(event.origin)) {
      console.warn('Ignoring message from unknown origin:', event.origin);
      return;
    }

    if (event.data.type === 'AUTH_DATA') {
      console.log('Received auth data via postMessage:', event.data.data);

      // Store the auth data
      if (storeAuthData(event.data.data)) {
        callback(event.data.data);
      }
    }
  };

  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
};

/**
 * Initialize auth bridge - tries multiple methods to get auth data
 */
export const initializeAuthBridge = () => {
  return new Promise((resolve) => {
    let resolved = false;

    const resolveOnce = (data) => {
      if (!resolved) {
        resolved = true;
        resolve(data);
      }
    };

    // Method 1: Try to extract from URL parameters
    const urlAuthData = extractAuthFromURL();
    if (urlAuthData && urlAuthData.api_key) {
      console.log('Auth data found in URL parameters');
      storeAuthData(urlAuthData);
      cleanURLParams();
      resolveOnce(urlAuthData);
      return;
    }

    // Method 2: Check if auth data already exists in localStorage
    const existingApiKey = localStorage.getItem('api_key');
    if (existingApiKey) {
      console.log('Existing auth data found in localStorage');
      const existingAuthData = {
        api_key: existingApiKey,
        email: localStorage.getItem('email'),
        first_name: localStorage.getItem('first_name'),
        last_name: localStorage.getItem('last_name'),
        userData: JSON.parse(localStorage.getItem('user-data') || '{}')
      };
      resolveOnce(existingAuthData);
      return;
    }

    // Method 3: Listen for postMessage from parent window
    console.log('Waiting for auth data via postMessage...');
    const cleanup = setupPostMessageListener((data) => {
      cleanup();
      resolveOnce(data);
    });

    // Method 4: Timeout fallback
    setTimeout(() => {
      cleanup();
      if (!resolved) {
        console.log('No auth data received, user needs to login');
        resolveOnce(null);
      }
    }, 3000);
  });
};

/**
 * Validate auth data
 */
export const validateAuthData = (authData) => {
  if (!authData) return false;

  // Check if API key exists
  if (!authData.api_key) return false;

  // Add more validation as needed
  return true;
};

/**
 * Redirect to internal login page
 */
export const redirectToLogin = () => {
  console.log('Redirecting to internal login page');
  window.location.href = '/login';
};
